import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useReducedMotion } from 'motion/react';
import { cn } from '@/src/infrastructure/lib/utils';

export interface ParallaxLayerProps {
  children: React.ReactNode;
  className?: string;
  speed?: number; // negative for slower/background, positive for faster/foreground
  disabled?: boolean;
}

export function ParallaxLayer({ children, className, speed = -0.15, disabled = false }: ParallaxLayerProps) {
  const ref = useRef<HTMLDivElement>(null);
  const shouldReduceMotion = useReducedMotion();
  
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });

  // Bound vertical offset to [-10%, 10%] max so image stays fully visible within inset
  const clampedSpeed = Math.max(-0.25, Math.min(0.25, speed));
  const y = useTransform(scrollYProgress, [0, 1], [`${-clampedSpeed * 40}%`, `${clampedSpeed * 40}%`]);

  return (
    <div ref={ref} className={cn("relative w-full h-full overflow-hidden z-0", className)}>
      <motion.div
        style={{ y: shouldReduceMotion || disabled ? 0 : y }}
        className="absolute -top-[12%] -left-[12%] w-[124%] h-[124%] pointer-events-none"
      >
        {children}
      </motion.div>
    </div>
  );
}
